import React, { useCallback, useEffect, useMemo, useState } from "react";

import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import BinIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import EmptyCardDataOperationsData from "assets/images/EmptyCardDataOperationsData.png";
import { AsyncContentMui, ProgressOverlay, RoleComponentGuard } from "components";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { ActionRoleGuard } from "components/guards/Action-role.guard";
import { NavigateLink } from "components/Navigate-link/NavigateLink";
import { FileViewer } from "components/popups";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useActionHook, useDidUpdate, useFormatDateHook, useModals, useNavigate } from "hooks";
import {
  IApiServiceError,
  useCommitmentsSelector,
  useCreateManyMeasureDataSelect,
  useDealActions,
  useMeasureParametersSelector,
  useMonitoringDataFilters,
  usePickUserPermissions,
  useRoundsSelector,
  useViewActions
} from "store";
import { MeasureDataDraft, MeasureParameter, MonitoringDataFiles } from "store/types";

import { FileType, RoleName } from "../../../constants";
import { Group } from "../Groups-form/GroupsFormContext";

const commonCellStyles: TableCellProps["sx"] = {
  fontSize: "15px",
  color: "#013951",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  maxWidth: "250px",
  height: "50px"
};

const cellHeaderStyles: TableCellProps["sx"] = {
  backgroundColor: "#edf1f9",
  color: "#0a3142",
  fontSize: "15px",
  fontWeight: "normal",
  border: "none",
  whiteSpace: "nowrap"
};

interface Props {
  dealId?: number;
  dealTitle?: string;
  setIsEmpty?: (value: boolean) => void;
}

export const DraftsTable: React.FC<Props> = ({ dealId, dealTitle, setIsEmpty }) => {
  const dealRoundsIdsCtx: number[] = useRoundsSelector().map((item) => item.id);
  const dealCommitsIdsCtx: number[] = useCommitmentsSelector().map((item) => item.id);
  const dataFilters = useMonitoringDataFilters();
  const measures = useMeasureParametersSelector();
  const createMeasureData = useCreateManyMeasureDataSelect();
  const dealMetrics = usePickUserPermissions("deal_metrics");
  const isViewerPermission = dealMetrics && dealMetrics === "viewer";
  const navigate = useNavigate();
  const [pdfForViewer, setPdfForViewer] = useState<{ file_url: string; type: FileType; name: string } | null>(null);
  const [filesMenuState, setFilesMenuState] = useState<{ anchorEl: HTMLElement; rowId: number } | null>(null);

  //table use for deal and data operation, for data operation we need deal column
  const columns: string[] = useMemo(() => {
    const columns = ["Parameters", "Created by", "Created at", "", ""];
    if (!dealId) {
      columns.unshift("Deal");
    }
    return columns;
  }, [dealId]);

  const [inspectLoading, setInspectLoading] = useState<boolean>(false);

  const measuresMap: Map<number, MeasureParameter> = useMemo(() => {
    const result = new Map();

    if (!measures) return result;

    measures.forEach((measure) => result.set(measure.id, measure));

    return result;
  }, [measures]);

  const { errorModal, deleteModal } = useModals();
  const formatDate = useFormatDateHook();
  const { setCreateManyMeasureData, setIsDealCharBarOpenAction } = useViewActions();
  const { getMeasureDataDraftAction, getMeasureDataDraftsAction, deleteMeasureDataDraftAction } = useDealActions();

  const {
    action: getDrafts,
    loading,
    data: drafts = []
  } = useActionHook(() => getMeasureDataDraftsAction(dealId ? [dealId] : dataFilters.deals.map((item) => item.id)), {
    onSuccess: (result) => {
      setIsDealCharBarOpenAction(Boolean(result.length));
      setIsEmpty?.(!result.length);
    }
  });

  useEffect(() => {
    getDrafts();
  }, []);

  useDidUpdate(() => {
    getDrafts();
  }, [dataFilters.deals]);

  useDidUpdate(() => {
    if (createMeasureData) return;
    getDrafts();
  }, [createMeasureData]);

  const inspectDraft = useCallback(
    async (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: any) => {
      try {
        setInspectLoading(true);
        const target = e.target as Element;
        if (!target.classList.contains("draft-inspect-box")) return;

        const { id: draftId } = row;

        const {
          id,
          deal_id,
          data: { group, measure_data, rounds }
        } = await getMeasureDataDraftAction(draftId);
        setCreateManyMeasureData({
          dealId: deal_id,
          dealTitle: dealTitle,
          initialGroups: group,
          initialMeasureData: measure_data,
          initialRounds: rounds || {},
          draftId: id
        });
      } catch (error) {
        errorModal({ msg: (error as Error).message });
      } finally {
        setInspectLoading(false);
      }
    },
    [errorModal, dealRoundsIdsCtx, dealCommitsIdsCtx, dealTitle]
  );

  const onDeleteDraft = useCallback(
    (id: number) => {
      deleteModal({
        element: "Delete Draft",
        action: async () => {
          try {
            await deleteMeasureDataDraftAction(id);
            getDrafts();
          } catch (error) {
            errorModal({ msg: (error as IApiServiceError).message });
          }
        }
      });
    },
    [deleteModal, errorModal]
  );

  const buildParamsString = useCallback(
    (groups: Group[]): string => {
      let str = "";
      groups.forEach(({ measure_id }, index) => {
        if (!measure_id) return;
        const measureName: string | undefined = measuresMap.get(measure_id)?.name;
        if (measureName) {
          str += index !== groups.length - 1 ? `${measureName}, ` : `${measureName}`;
        }
      });
      if (!str) {
        str = "Params";
      }
      return str;
    },
    [measuresMap]
  );

  const getFilesFromDraft = useCallback((row: MeasureDataDraft) => {
    const filesFromGroups = row.data.group.flatMap((group) =>
      Object.values(group.files?.dbFiles || {}).map((file) => ({
        name: file.name,
        file_url: file.file_url,
        id: file.id
      }))
    );

    const filesFromMeasureData = (row.data.measure_data || []).flatMap((measureData) =>
      Object.values(measureData.files?.dbFiles || {}).map((file) => ({
        name: file.name,
        file_url: file.file_url,
        id: file.id
      }))
    );

    return [...filesFromGroups, ...filesFromMeasureData];
  }, []);

  return (
    <AsyncContentMui spinnerProps={{ size: 200 }} isLoading={loading}>
      {!drafts.length ? (
        <EmptyCard
          title={EMPTY_CARD_CONTENT.draftsTable.title}
          description={{
            text: EMPTY_CARD_CONTENT.draftsTable.description,
            externalText: EMPTY_CARD_CONTENT.draftsTable.externalDescription,
            sx: { width: "380px" }
          }}
          image={{
            src: EmptyCardDataOperationsData,
            sx: { width: 315, height: 140 }
          }}
        />
      ) : (
        <ProgressOverlay containerSX={{ height: "100%", overflow: "hidden" }} inProgress={inspectLoading}>
          <TableContainer
            sx={{
              borderRadius: "8px",
              border: "1px solid #dae0e2",
              borderTop: "none",
              paddingBottom: "7px",
              backgroundColor: "white",
              height: "100%"
            }}
          >
            <Table
              sx={{
                ".MuiTableCell-root": {
                  padding: "8px 26px",
                  ":last-child": { width: "4%" }
                }
              }}
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  {columns.map((item, index) => (
                    <TableCell sx={cellHeaderStyles} key={item} align={!index ? "left" : "center"}>
                      {item}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {drafts.map((row) => {
                  const files = getFilesFromDraft(row);
                  return (
                    <ActionRoleGuard key={row.id} availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                      <TableRow
                        onClick={(e) => inspectDraft(e, row)}
                        key={row.id}
                        sx={{
                          ":hover": { backgroundColor: "#e8f1f1", ".icon": { opacity: 1 } },
                          cursor: "pointer",
                          ".icon": {
                            color: "#0a3142",
                            transition: "opacity 0.3s linear",
                            opacity: 0,
                            "&.files-icon": {
                              color: "#26A6AE"
                            }
                          }
                        }}
                      >
                        {!dealId && (
                          <TableCell
                            className="draft-inspect-box"
                            sx={{ ...commonCellStyles, width: "200px", maxWidth: "200px" }}
                            align="left"
                          >
                            <MaterialTooltip title={row.deal_title} isShow>
                              <span
                                style={{
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "block"
                                }}
                              >
                                <NavigateLink
                                  allowNewTab={true}
                                  route="dealDashboard"
                                  params={{ deal_id: String(row.deal_id) }}
                                  style={{ color: "#26a6ae" }}
                                >
                                  <>{row.deal_title}</>
                                </NavigateLink>
                              </span>
                            </MaterialTooltip>
                          </TableCell>
                        )}
                        <TableCell
                          className="draft-inspect-box"
                          sx={commonCellStyles}
                          component="th"
                          scope="row"
                          align={!dealId ? "center" : "left"}
                        >
                          <MaterialTooltip title={buildParamsString(row.data.group || [])} isShow>
                            <Typography noWrap>{buildParamsString(row.data.group || [])}</Typography>
                          </MaterialTooltip>
                        </TableCell>
                        <TableCell
                          className="draft-inspect-box"
                          sx={{ ...commonCellStyles, width: "200px", maxWidth: "200px" }}
                          align="center"
                        >
                          <MaterialTooltip title={row.created_by} isShow>
                            <Typography noWrap>{row.created_by}</Typography>
                          </MaterialTooltip>
                        </TableCell>
                        <TableCell
                          className="draft-inspect-box"
                          sx={{ ...commonCellStyles, width: "200px", maxWidth: "200px" }}
                          align="center"
                        >
                          {formatDate({ date: row.created_at })}
                        </TableCell>
                        <TableCell sx={commonCellStyles}>
                          <RoleComponentGuard
                            availableRoles={[RoleName.Admin, RoleName.Analyst]}
                            hideChild={isViewerPermission}
                          >
                            <Stack direction="row" gap="5px" alignItems="center">
                              <IconButton size="sm">
                                <BinIcon
                                  className="icon"
                                  sx={{ cursor: "pointer" }}
                                  onClick={() => onDeleteDraft(row.id)}
                                />
                              </IconButton>
                              {Boolean(files?.length) && (
                                <>
                                  <IconButton
                                    onClick={(event) => {
                                      setFilesMenuState({ anchorEl: event.currentTarget, rowId: row.id });
                                    }}
                                    size="sm"
                                  >
                                    <DescriptionOutlinedIcon className="icon files-icon" />
                                  </IconButton>
                                  <Typography className="icon files-icon">+{files?.length}</Typography>
                                  <Menu
                                    sx={{ p: "10px" }}
                                    anchorEl={filesMenuState?.anchorEl}
                                    open={filesMenuState?.rowId === row.id}
                                    onClose={() => setFilesMenuState(null)}
                                  >
                                    {files?.map((file: MonitoringDataFiles) => (
                                      <MenuItem
                                        key={file.id}
                                        onClick={() => {
                                          setPdfForViewer({
                                            file_url: file.file_url,
                                            type: FileType.PDF,
                                            name: file.name
                                          });
                                          setFilesMenuState(null);
                                        }}
                                      >
                                        <Stack direction="row" gap="10px">
                                          <DescriptionOutlinedIcon />
                                          <MaterialTooltip isShow title={file.name}>
                                            <Typography
                                              sx={{
                                                maxWidth: "150px",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis"
                                              }}
                                            >
                                              {file.name}
                                            </Typography>
                                          </MaterialTooltip>
                                        </Stack>
                                      </MenuItem>
                                    ))}
                                  </Menu>
                                </>
                              )}
                            </Stack>
                          </RoleComponentGuard>
                        </TableCell>
                        <TableCell sx={commonCellStyles} align="center">
                          <RoleComponentGuard
                            availableRoles={[RoleName.Admin, RoleName.Analyst]}
                            hideChild={isViewerPermission}
                          >
                            {!!row.tags?.length && (
                              <MaterialTooltip
                                isShow
                                title={
                                  <>
                                    Open <strong>{row.tags.map((tag) => tag.file_name).join(", ")}</strong> in PDF
                                    Tagger
                                  </>
                                }
                                placement="top"
                              >
                                <IconButton
                                  onClick={() => {
                                    if (!row.tags) return;
                                    navigate({
                                      route: "tagger",
                                      search: {
                                        filesIds: row.tags.map((tag) => tag.file_id),
                                        sections: row.tags.map((tag) => tag.section_id),
                                        dealId: row.deal_id
                                      },
                                      setQueriesMethod: "replace"
                                    });
                                  }}
                                >
                                  <ArticleOutlinedIcon />
                                </IconButton>
                              </MaterialTooltip>
                            )}
                          </RoleComponentGuard>
                        </TableCell>
                      </TableRow>
                    </ActionRoleGuard>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {pdfForViewer?.file_url && (
            <FileViewer
              file={pdfForViewer}
              isOpen={Boolean(pdfForViewer)}
              onClose={() => {
                setPdfForViewer(null);
              }}
            />
          )}
        </ProgressOverlay>
      )}
    </AsyncContentMui>
  );
};
